import React, { useState, useMemo } from 'react';
import { PageHeader, Input, Space, List, Avatar, Tag, Tooltip, Divider } from 'antd';
import {
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LockOutlined,
  SafetyOutlined,
  IdcardOutlined,
} from '@ant-design/icons';

import CenteredSpinner from '../../../components/CenteredSpinner';
import { useStrikeAdmins } from '../../../models/useStrikeAdmins';
import { getAppBaseUrl } from '../../../utils/URLBuilder';

import '../../../App.scss';
import 'antd/dist/antd.css';

const URLPath = getAppBaseUrl();

function formatAuthProviders(user) {
  if (!user) return [];
  if (Array.isArray(user.authProviders) && user.authProviders.length > 0) return user.authProviders;
  if (user.authProvider) return [user.authProvider];
  return [];
}

function AdminCard({ userId, role, user, client }) {
  const displayName = user?.displayName || client?.displayName || userId;
  const email = user?.email || user?.authEmail || null;
  const phone = user?.phoneNumber || null;
  const avatar = user?.photoURL || client?.avatar || null;
  const providers = formatAuthProviders(user);
  const clientHref = client ? `${URLPath}/admin/clients/${client.id}` : null;

  return (
    <List.Item style={{ padding: '12px 0', display: 'block' }}>
      {/* Top row: avatar + name + role/status tags */}
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space align="start">
          <Avatar src={avatar} icon={!avatar && <UserOutlined />} size={40}>
            {!avatar && displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Space direction="vertical" size={2}>
            {clientHref ? (
              <a href={clientHref} style={{ fontWeight: 600, fontSize: 14 }}>{displayName}</a>
            ) : (
              <Text strong style={{ fontSize: 14 }}>{displayName}</Text>
            )}

            {/* Email or phone */}
            {email ? (
              <Space size={4} style={{ fontSize: 12, color: '#555' }}>
                <MailOutlined />
                <span>{email}</span>
                {user?.emailVerified ? (
                  <Tooltip title="Verified">
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Not verified">
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  </Tooltip>
                )}
              </Space>
            ) : phone ? (
              <Space size={4} style={{ fontSize: 12, color: '#555' }}>
                <PhoneOutlined />
                <span>{phone}</span>
              </Space>
            ) : null}
          </Space>
        </Space>

        {/* Role + client status tags (top-right on wide, wraps on narrow) */}
        <Space size={4} wrap>
          <Tag color="red" icon={<SafetyOutlined />} style={{ margin: 0 }}>{role || 'Admin'}</Tag>
          {client?.status && (
            <Tag color={client.status === 'live' ? 'green' : 'orange'} style={{ margin: 0 }}>
              {client.status}
            </Tag>
          )}
        </Space>
      </Space>

      {/* Auth providers */}
      {providers.length > 0 && (
        <Space size={4} wrap style={{ marginTop: 8 }}>
          <LockOutlined style={{ fontSize: 11, color: '#999' }} />
          {providers.map((p) => (
            <Tag key={p} style={{ fontSize: 11, margin: 0 }}>{p}</Tag>
          ))}
        </Space>
      )}

      {/* Links row: userId only */}
      <Space size={4} style={{ marginTop: 8, fontSize: 11 }}>
        <IdcardOutlined style={{ color: '#999' }} />
        <a href={`${URLPath}/admin/users/${userId}`}>
          <code style={{ fontSize: 11 }}>{userId}</code>
        </a>
      </Space>
    </List.Item>
  );
}

export default function AdminAdminsScreen() {
  const [searchText, setSearchText] = useState('');
  const [loading, admins] = useStrikeAdmins();

  const filtered = useMemo(() => {
    const list = admins || [];
    const q = (searchText || '').toLowerCase().trim();
    if (!q) return list;
    return list.filter(({ userId, user, client }) => {
      const haystack = [
        userId,
        user?.email,
        user?.displayName,
        user?.phoneNumber,
        user?.authProvider,
        client?.id,
        client?.displayName,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [admins, searchText]);

  return (
    <div style={{ height: '100%' }}>
      <PageHeader
        ghost={false}
        title="Strike Admins"
        subTitle="Users in /clients/strike/admins"
      />

      <div style={{ padding: '0 16px 16px' }}>
        <Input.Search
          placeholder="Search name, email, userId, clientId..."
          allowClear
          style={{ maxWidth: 440, marginBottom: 8 }}
          onSearch={setSearchText}
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
        />
        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
          {(filtered || []).length} admin{(filtered || []).length !== 1 ? 's' : ''}
        </div>
        <Divider style={{ margin: '8px 0' }} />

        {loading ? (
          <CenteredSpinner loading={true} noLayout={false} />
        ) : (
          <List
            dataSource={filtered}
            rowKey="userId"
            renderItem={(admin) => <AdminCard {...admin} />}
            locale={{ emptyText: 'No admins found' }}
          />
        )}
      </div>
    </div>
  );
}
